/**
 * ============================================================================
 * TASK DEPENDENCY SERVICE - PHASE 4 COMPLIANT
 * ============================================================================
 *
 * Facade coordinating focused task-dependency sub-services.
 *
 * @phase 4
 * @module services/features/task-dependency
 */

import { IService, ServiceResponse } from "@/services/core/service-registry";
import { logger } from "@/lib/logger";
import type {
  CreateTaskDependencyRequest,
  DependencyGraph,
  TaskDependency,
  UpdateTaskDependencyRequest,
} from "@/types/time-management";
import { TaskDependencyCrudService } from "./task-dependency-crud.service";
import { TaskDependencyValidationService } from "./task-dependency-validation.service";
import { TaskDependencyCriticalPathService } from "./task-dependency-critical-path.service";
import { TaskDependencyGraphService } from "./task-dependency-graph.service";

export { TaskDependencyCrudService } from "./task-dependency-crud.service";
export { TaskDependencyValidationService } from "./task-dependency-validation.service";
export { TaskDependencyCriticalPathService } from "./task-dependency-critical-path.service";
export { TaskDependencyGraphService } from "./task-dependency-graph.service";

/**
 * Minimal facade interface for task dependency.
 * Sub-services can be imported directly for advanced operations.
 */
export interface ITaskDependencyService extends IService {
  getAllDependencies(): Promise<ServiceResponse<TaskDependency[]>>;
  getTaskDependencies(taskId: string): Promise<
    ServiceResponse<{
      predecessors: TaskDependency[];
      successors: TaskDependency[];
    }>
  >;
  createDependency(
    request: CreateTaskDependencyRequest
  ): Promise<ServiceResponse<TaskDependency>>;
  checkCircularDependency(
    predecessorId: string,
    successorId: string
  ): Promise<ServiceResponse<boolean>>;
  buildDependencyGraph(
    taskIds: string[]
  ): Promise<ServiceResponse<DependencyGraph>>;
  identifyCriticalTasks(projectId: string): Promise<ServiceResponse<string[]>>;
}

/**
 * Facade for task dependency services.
 * Delegates to focused sub-services for CRUD, validation, graph, and critical path.
 */
export class TaskDependencyService implements ITaskDependencyService {
  public readonly name = "TaskDependencyService";
  private initialized = false;
  private crudService: TaskDependencyCrudService;
  private validationService: TaskDependencyValidationService;
  private criticalPathService: TaskDependencyCriticalPathService;
  private graphService: TaskDependencyGraphService;

  constructor() {
    this.crudService = new TaskDependencyCrudService();
    this.validationService = new TaskDependencyValidationService();
    this.criticalPathService = new TaskDependencyCriticalPathService();
    this.graphService = new TaskDependencyGraphService(
      this.validationService,
      this.criticalPathService
    );
  }

  async initialize(): Promise<void> {
    if (this.initialized) {return;}

    try {
      logger.info("📊 Initializing Task Dependency Service (Phase 4)...");
      await this.crudService.initialize();
      await this.validationService.initialize();
      await this.criticalPathService.initialize();
      await this.graphService.initialize();
      this.initialized = true;
      logger.info("✅ Task Dependency Service initialized");
    } catch (error) {
      logger.error("❌ Task Dependency Service initialization failed:", { error: error });
      throw error;
    }
  }

  async dispose(): Promise<void> {
    this.initialized = false;
    await this.crudService.dispose();
    await this.validationService.dispose();
    await this.criticalPathService.dispose();
    await this.graphService.dispose();
    logger.info("🔌 Task Dependency Service disposed");
  }

  async getAllDependencies(): Promise<ServiceResponse<TaskDependency[]>> {
    return this.crudService.getAllDependencies();
  }

  async getTaskDependencies(taskId: string): Promise<
    ServiceResponse<{
      predecessors: TaskDependency[];
      successors: TaskDependency[];
    }>
  > {
    return this.crudService.getTaskDependencies(taskId);
  }

  async createDependency(
    request: CreateTaskDependencyRequest
  ): Promise<ServiceResponse<TaskDependency>> {
    const validationResult = await this.validationService.validateDependency(request);
    if (!validationResult.success) {
      return { success: false, error: validationResult.error };
    }

    if (!validationResult.data?.isValid) {
      return {
        success: false,
        error:
          validationResult.data?.errors?.join(", ") ||
          "Dependency validation failed",
      };
    }

    return this.crudService.createDependency(request);
  }

  async checkCircularDependency(
    predecessorId: string,
    successorId: string
  ): Promise<ServiceResponse<boolean>> {
    return this.validationService.checkCircularDependency(
      predecessorId,
      successorId
    );
  }

  async buildDependencyGraph(
    taskIds: string[]
  ): Promise<ServiceResponse<DependencyGraph>> {
    return this.graphService.buildDependencyGraph(taskIds);
  }

  async identifyCriticalTasks(
    projectId: string
  ): Promise<ServiceResponse<string[]>> {
    return this.criticalPathService.identifyCriticalTasks(projectId);
  }
}

export const taskDependencyService = new TaskDependencyService();
export { taskDependencyService as default };
